import { useCallback, useState } from 'react';
import { Switch } from 'antd';
import type { TableProps } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Siren } from 'lucide-react';

import { DataTable, MenuThaoTac } from '../../../../components/shared';
import { Badge, Button, Dialog, Empty } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { MUC_DO_THONG_BAO } from '../../constants';
import { useChuQuyTac } from '../../hooks/useChuQuyTac';
import { batTatQuyTac, suaQuyTac, themQuyTac, xoaQuyTac } from '../../nghiep-vu/quy-tac-canh-bao';
import type { LoiMayChuQuyTac } from '../../nghiep-vu/quy-tac-canh-bao';
import type { BienHieuLuc, QuyTacCanhBao } from '../../types';
import { NganQuyTacCanhBao } from './NganQuyTacCanhBao';

interface DongBang extends QuyTacCanhBao {
  _i: number;
}

/**
 * Ô kiểu `alert_rule_list`: bảng quy tắc cảnh báo audit (ADR-0059 mục 4.1).
 *
 * Mọi thao tác — thêm, sửa, xoá, bật tắt — chỉ đổi **bản nháp** của form cài
 * đặt; lưu qua thanh lưu chung với `If-Match`, như mọi khoá khác. Không có
 * quyền sửa thì bảng vẫn hiện để đọc, nhưng nút thêm và menu thao tác ẩn hẳn,
 * công tắc chỉ đọc (ADR-UI-0005).
 */
export function BangQuyTacCanhBao({
  id,
  ten,
  giaTri,
  onDoi,
  chiDoc,
  bien,
  loiMayChu,
}: {
  id?: string;
  /** Nhãn của tham số, ghép vào caption. */
  ten: string;
  giaTri: QuyTacCanhBao[];
  onDoi: (ds: QuyTacCanhBao[]) => void;
  chiDoc: boolean;
  bien: BienHieuLuc | null;
  /** Lỗi theo trường của server, theo chỉ số quy tắc trong bản vừa gửi. */
  loiMayChu: Map<number, LoiMayChuQuyTac[]>;
}) {
  const { t, locale } = useT();
  const chu = useChuQuyTac();
  const [ngan, setNgan] = useState<{ mo: boolean; dangSua: QuyTacCanhBao | null; lan: number }>({
    mo: false,
    dangSua: null,
    lan: 0,
  });
  const [hoiXoa, setHoiXoa] = useState<QuyTacCanhBao | null>(null);

  const toiDa = bien?.max_items ?? null;
  const daDu = toiDa !== null && giaTri.length >= toiDa;
  const dong: DongBang[] = giaTri.map((q, i) => ({ ...q, _i: i }));

  const moNgan = useCallback((dangSua: QuyTacCanhBao | null) => {
    setNgan((n) => ({ mo: true, dangSua, lan: n.lan + 1 }));
  }, []);

  const nutThem = !chiDoc && (
    <Button variant="outline" size="sm" icon={<PlusOutlined />} disabled={daDu} onClick={() => moNgan(null)}>
      {t('quyTacCanhBao.them')}
    </Button>
  );

  const cot: TableProps<DongBang>['columns'] = [
    {
      title: t('quyTacCanhBao.cotTen'),
      width: 200,
      render: (_v, row) => {
        const loi = loiMayChu.get(row._i) ?? [];
        return (
          <div className="flex min-w-0 max-w-xs flex-col">
            <span className="truncate text-sm font-medium text-content-primary">{row.name}</span>
            <span className="truncate font-mono text-2xs text-content-muted">{row.id}</span>
            {loi.map((l, j) => (
              <span key={j} role="alert" className="mt-0.5 text-xs text-danger-700">
                {l.truong ? t('quyTacCanhBao.loiTruong', { truong: chu.tenTruong(l.truong), cau: l.cau }) : l.cau}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: t('quyTacCanhBao.cotDieuKien'),
      width: 240,
      render: (_v, row) => <span className="line-clamp-2 text-xs text-content-secondary">{chu.dieuKien(row)}</span>,
    },
    {
      title: t('quyTacCanhBao.cotNguong'),
      width: 180,
      render: (_v, row) => <span className="text-xs text-content-secondary">{chu.nguong(row)}</span>,
    },
    {
      title: t('quyTacCanhBao.cotMuc'),
      dataIndex: 'severity',
      width: 120,
      render: (v: QuyTacCanhBao['severity']) => (
        <Badge variant={MUC_DO_THONG_BAO[v]?.variant ?? 'neutral'}>
          {MUC_DO_THONG_BAO[v] ? t(MUC_DO_THONG_BAO[v].khoa) : v}
        </Badge>
      ),
    },
    {
      title: t('quyTacCanhBao.cotBat'),
      width: 90,
      render: (_v, row) => (
        <Switch
          size="small"
          checked={row.enabled}
          disabled={chiDoc}
          aria-label={t('quyTacCanhBao.batTatVoi', { ten: row.name })}
          onChange={(bat) => onDoi(batTatQuyTac(giaTri, row.id, bat))}
        />
      ),
    },
    ...(chiDoc
      ? []
      : [
          {
            title: t('chung.thaoTac'),
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, row: DongBang) => (
              <MenuThaoTac
                tenBanGhi={row.name}
                muc={[
                  {
                    khoa: 'sua',
                    nhan: t('quyTacCanhBao.sua'),
                    icon: <EditOutlined />,
                    onChon: () => moNgan(giaTri[row._i] ?? null),
                  },
                  {
                    khoa: 'xoa',
                    nhan: t('quyTacCanhBao.xoa'),
                    icon: <DeleteOutlined />,
                    nhom: 'pha-huy',
                    onChon: () => setHoiXoa(row),
                  },
                ]}
              />
            ),
          },
        ]),
  ];

  return (
    <div id={id} className="space-y-2">
      {!chiDoc && giaTri.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-content-muted">
            {toiDa !== null
              ? t('quyTacCanhBao.demToiDa', { so: soVN(giaTri.length, locale), toiDa: soVN(toiDa, locale) })
              : t('caiDat.soQuyTac', { so: soVN(giaTri.length, locale) })}
          </span>
          {nutThem}
        </div>
      )}
      <div className="overflow-hidden rounded border border-default">
        <DataTable<DongBang>
          caption={t('quyTacCanhBao.caption', { ten })}
          rowKey="id"
          dataSource={dong}
          columns={cot}
          size="small"
          pagination={false}
          locale={{
            emptyText: (
              <Empty
                icon={Siren}
                title={t('quyTacCanhBao.rong')}
                description={t('quyTacCanhBao.rongMo')}
                action={nutThem || undefined}
              />
            ),
          }}
        />
      </div>

      {!chiDoc && (
        <NganQuyTacCanhBao
          key={ngan.lan}
          open={ngan.mo}
          dangSua={ngan.dangSua}
          dsHienCo={giaTri}
          bien={bien}
          loiMayChu={ngan.dangSua ? (loiMayChu.get(giaTri.findIndex((q) => q.id === ngan.dangSua!.id)) ?? []) : []}
          onClose={() => setNgan((n) => ({ ...n, mo: false }))}
          onLuu={(q) => {
            onDoi(ngan.dangSua ? suaQuyTac(giaTri, ngan.dangSua.id, q) : themQuyTac(giaTri, q));
            setNgan((n) => ({ ...n, mo: false }));
          }}
        />
      )}

      <Dialog
        open={hoiXoa !== null}
        onClose={() => setHoiXoa(null)}
        title={t('quyTacCanhBao.xoaTieuDe', { ten: hoiXoa?.name ?? '' })}
        width={480}
        destroyOnHidden
        footer={
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={() => setHoiXoa(null)}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="destructive"
              size="md"
              onClick={() => {
                if (hoiXoa) onDoi(xoaQuyTac(giaTri, hoiXoa.id));
                setHoiXoa(null);
              }}
            >
              {t('quyTacCanhBao.xoa')}
            </Button>
          </div>
        }
      >
        <p className="pt-2 text-sm leading-relaxed text-content-secondary">{t('quyTacCanhBao.xoaThan')}</p>
      </Dialog>
    </div>
  );
}
